import { SprintAPI } from '../../utils/api';
import { calculateNextNodePosition } from '../../utils/nodePositioning';
import { dispatchTasksUpdatedEvent } from '../campaignNesting/utils';

/**
 * Re-link or recreate the parent mission node that anchors a restored nested campaign.
 * Returns the mission task id used for "go to" navigation, or null.
 */
export async function relinkOrRecreateParentMission({
  originalParent,
  fullSprint,
  sprintId,
  taskIdToUse,
  setSprints,
}) {
  if (!originalParent) return null;

  try {
    const freshParent = await SprintAPI.get(originalParent.id);
    if (!freshParent) return null;

    const existingLinkTask = freshParent.tasks?.find((t) => t.nestedSprintId === sprintId);
    const parentTask = fullSprint._originalParentTaskId
      ? freshParent.tasks?.find((t) => t.id === fullSprint._originalParentTaskId)
      : null;

    if (existingLinkTask) {
      return existingLinkTask.id;
    }

    if (parentTask && !parentTask.nestedSprintId) {
      return relinkExistingTask({
        freshParent,
        originalParentId: originalParent.id,
        originalTaskId: fullSprint._originalParentTaskId,
        sprintId,
        setSprints,
      });
    }

    if (!parentTask || !fullSprint._originalParentTaskId) {
      return recreateMissionNode({
        freshParent,
        originalParentId: originalParent.id,
        fullSprint,
        sprintId,
        taskIdToUse,
        setSprints,
      });
    }
  } catch (error) {
    console.error('Failed to update parent sprint:', error);
  }

  return null;
}

async function relinkExistingTask({
  freshParent,
  originalParentId,
  originalTaskId,
  sprintId,
  setSprints,
}) {
  const newTasks = freshParent.tasks.map((t) =>
    t.id === originalTaskId
      ? { ...t, nestedSprintId: sprintId }
      : t
  );
  const updatedParent = {
    ...freshParent,
    tasks: newTasks,
  };
  setSprints((prev) => prev.map((s) => (s.id === originalParentId ? updatedParent : s)));
  await SprintAPI.update(originalParentId, updatedParent);
  console.log('✅ Re-linked existing task to restored sprint');
  dispatchTasksUpdatedEvent(originalParentId, newTasks, freshParent.nodePositions);
  return originalTaskId;
}

async function recreateMissionNode({
  freshParent,
  originalParentId,
  fullSprint,
  sprintId,
  taskIdToUse,
  setSprints,
}) {
  // Task was deleted OR we don't have the original task ID (backwards compatibility).
  // Recreate the mission node with the nestedSprintId.
  // This happens when user deletes a mission node (which also trashes the nested campaign)
  // and then restores the campaign from trash.
  // Use the same approach as useNestCampaign for consistency.
  const existingTasks = freshParent.tasks || [];
  const existingPositions = freshParent.nodePositions || {};
  const newPosition = calculateNextNodePosition(existingTasks, existingPositions);

  const recreatedTask = {
    id: taskIdToUse,
    name: fullSprint.name || 'Restored Campaign',
    status: 'pending',
    nestedSprintId: sprintId,
    createdAt: new Date().toISOString(),
    x: newPosition.x,
    y: newPosition.y,
    ...(fullSprint.icon && { icon: fullSprint.icon }),
  };

  const newTasks = [...existingTasks, recreatedTask];
  const newPositions = {
    ...existingPositions,
    [recreatedTask.id]: newPosition,
  };

  const updatedParent = {
    ...freshParent,
    tasks: newTasks,
    nodePositions: newPositions,
  };

  setSprints((prev) => prev.map((s) => (s.id === originalParentId ? updatedParent : s)));
  await SprintAPI.update(originalParentId, updatedParent);
  console.log('✅ Recreated mission node for restored nested campaign:', recreatedTask.name);

  // Dispatch event so SprintDetail updates its local state if viewing this campaign.
  // This is critical because SprintDetail has its own localSprint state that won't
  // automatically pick up changes to the global sprints array.
  dispatchTasksUpdatedEvent(originalParentId, newTasks, newPositions);
  return recreatedTask.id;
}
